import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUpRight, Loader2, Network, MapPin, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming','International'];

const SPECIALTIES = ['Luxury Residential', 'Flips', 'Commercial', 'Diaspora Buyers', 'Distressed Assets', 'New Construction', 'Marine / Resort'];

export default function BrokerPortal() {
  const [stats, setStats] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    state: 'California',
    years_experience: 5,
    license_number: '',
    brokerage: '',
    specialties: [],
    avg_deal_size_usd: '',
    why_heritage: '',
    referral_source: '',
  });

  useEffect(() => {
    axios.get(`${API}/brokers/network/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSpec = (s) => setForm((f) => ({
    ...f,
    specialties: f.specialties.includes(s) ? f.specialties.filter((x) => x !== s) : [...f.specialties, s]
  }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        years_experience: Number(form.years_experience),
        avg_deal_size_usd: form.avg_deal_size_usd ? Number(form.avg_deal_size_usd) : null,
      };
      const { data } = await axios.post(`${API}/brokers/apply`, payload, { withCredentials: true });
      setSubmitted(data);
      toast.success('Application transmitted. Mercy reviews within 7 days.');
    } catch {
      toast.error('Transmission refused. Verify your inputs.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-24 min-h-[70vh]">
        <CheckCircle2 size={40} className="text-[#D4AF37] mb-8" strokeWidth={1} />
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4">Reference · {submitted.id.slice(0, 8).toUpperCase()}</div>
        <h1 className="text-5xl md:text-6xl font-extralight leading-tight tracking-tight mb-6">
          Application<br /><span className="font-display italic text-[#D4AF37]">sealed.</span>
        </h1>
        <p className="text-base text-[#A3A3A3] font-light max-w-2xl">
          Your broker dossier is in Mercy's recruitment queue. The Sovereign Broker Network targets 50 states —
          we contact strong candidates within 7 business days.
        </p>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-12">
        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6 flex items-center gap-3">
            <Network size={14} strokeWidth={1.5} /> Sovereign Broker Network
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
            50 states.<br />
            <span className="font-display italic text-[#D4AF37]">One admiral.</span>
          </h1>
        </div>
        <div className="md:col-span-5 flex items-end">
          <p className="text-base text-[#A3A3A3] leading-relaxed font-light">
            Heritage Command is recruiting one sovereign broker per state to anchor the trans-atlantic corridor.
            Aggressive splits. Concierge support. Diaspora pipeline.
          </p>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16 border-t border-b border-[#D4AF37]/15 py-8">
          <Stat label="Target States" value={stats.target_states} />
          <Stat label="Activated" value={stats.activated} />
          <Stat label="In Pipeline" value={stats.in_pipeline} />
          <Stat label="Coverage" value={`${stats.coverage_pct}%`} />
        </div>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-8 space-y-10">
          <Section title="Identity">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Full Name" testId="broker-name" value={form.full_name} onChange={(v) => set('full_name', v)} required />
              <Field label="Email" type="email" testId="broker-email" value={form.email} onChange={(v) => set('email', v)} required />
              <Field label="Phone" testId="broker-phone" value={form.phone} onChange={(v) => set('phone', v)} required />
              <SelectField label="State" testId="broker-state" value={form.state} onChange={(v) => set('state', v)} options={US_STATES} />
            </div>
          </Section>

          <Section title="Credentials">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Years of Experience" testId="broker-years" type="number" value={form.years_experience} onChange={(v) => set('years_experience', v)} required />
              <Field label="Avg Deal Size (USD)" testId="broker-avg-deal" type="number" value={form.avg_deal_size_usd} onChange={(v) => set('avg_deal_size_usd', v)} />
              <Field label="License Number" testId="broker-license" value={form.license_number} onChange={(v) => set('license_number', v)} />
              <Field label="Current Brokerage" testId="broker-brokerage" value={form.brokerage} onChange={(v) => set('brokerage', v)} />
            </div>
          </Section>

          <Section title="Specialties">
            <div className="flex flex-wrap gap-3">
              {SPECIALTIES.map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => toggleSpec(s)}
                  data-testid={`broker-spec-${s.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`text-[10px] tracking-[0.3em] uppercase px-4 py-2 border transition-colors ${
                    form.specialties.includes(s) ? 'bg-[#D4AF37] text-[#050505] border-[#D4AF37]' : 'border-[#444] text-[#A3A3A3] hover:border-[#D4AF37]'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Motive">
            <div className="space-y-6">
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Why Heritage Command</label>
                <textarea
                  data-testid="broker-why"
                  rows={4}
                  className="input-sovereign resize-none"
                  value={form.why_heritage}
                  onChange={(e) => set('why_heritage', e.target.value)}
                  required
                  placeholder="Why this vessel, this corridor, this commander?"
                />
              </div>
              <Field label="Referral Source (optional)" testId="broker-referral" value={form.referral_source} onChange={(v) => set('referral_source', v)} />
            </div>
          </Section>

          <button type="submit" disabled={submitting} data-testid="broker-submit" className="btn-sovereign">
            {submitting ? <><Loader2 size={14} className="animate-spin" /> <span>Transmitting</span></> : <><span>Submit Application</span> <ArrowUpRight size={14} strokeWidth={1.5} /></>}
          </button>
        </div>

        <aside className="md:col-span-4">
          <div className="sticky top-28 sov-border bg-[#0A0A0A] p-6">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4 flex items-center gap-2">
              <MapPin size={12} strokeWidth={1.5} /> What you receive
            </div>
            <ul className="space-y-3 text-sm text-[#F5F5F5] font-light">
              <li className="border-b border-[#D4AF37]/15 pb-3">Aggressive Lineage Law splits</li>
              <li className="border-b border-[#D4AF37]/15 pb-3">Direct Wingman concierge for your investor leads</li>
              <li className="border-b border-[#D4AF37]/15 pb-3">Trans-atlantic corridor playbook</li>
              <li className="border-b border-[#D4AF37]/15 pb-3">State-exclusive geographic anchor (limited)</li>
              <li>Cadet Academy access for your team</li>
            </ul>
          </div>
        </aside>
      </form>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-5 border-b border-[#D4AF37]/15 pb-3">{title}</div>
    {children}
  </div>
);

const Field = ({ label, value, onChange, testId, type = 'text', required, placeholder }) => (
  <div>
    <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">{label}</label>
    <input data-testid={testId} type={type} className="input-sovereign" value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder} />
  </div>
);

const SelectField = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">{label}</label>
    <select data-testid={testId} className="input-sovereign cursor-pointer" value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => <option key={o}>{o}</option>)}
    </select>
  </div>
);

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">{label}</div>
    <div className="metric-number text-3xl md:text-4xl">{value}</div>
  </div>
);
